import React, { useEffect } from 'react';
import {
  Box,
  Heading,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Text,
  Metadata,
} from 'components';
import { useDispatch } from 'react-redux';
import * as routerActions from 'router/actions';
import { RouterLocation } from 'router';
import AvatarImage from './AvatarImage';
import { useAvatarsQuery } from './GetAvatars.generated';
import { useProfileContext } from './ProfileContext';
import { ProfilesLayout } from './Styles';
import { useOverflowContainerWidth } from 'utils/hooks';
import { ProfileType, PROFILE_AVATAR_SIZE, PROFILE_AVATAR_XS_SIZE } from './utils';

type ProfileSelectAvatarLocation = {
  params: {
    profileId?: string,
  },
  query?: {
    returnTo?: string,
  },
};

type ProfileSelectAvatarViewProps = {
  location: RouterLocation & ProfileSelectAvatarLocation,
};

function ProfileSelectAvatarView({ location }: ProfileSelectAvatarViewProps) {
  const dispatch = useDispatch();
  const {
    profiles,
    profilesLoading,
    profilesError,
    refreshProfiles,
    setEditProfile,
    editProfile,
    openReturnLocation,
  } = useProfileContext();

  const profileId = location?.params?.profileId;

  useEffect(() => {
    if (profilesError) {
      openReturnLocation();
    }
  }, [profilesError, openReturnLocation]);

  useEffect(() => {
    const profile = profiles?.find(p => p.id === profileId);

    if (profile?.id !== editProfile?.id) {
      setEditProfile(profile);
    }
  }, [profiles, profileId, editProfile?.id, setEditProfile]);

  const {
    data: avatarsData,
    loading: avatarsLoading,
    error: avatarsError,
  } = useAvatarsQuery();

  const avatars = avatarsData?.viewer.profileAvatars || [];
  const { containerRef, containerStyle } = useOverflowContainerWidth<HTMLDivElement>([avatars.length]);

  const navigateToEditProfile = () => {
    const targetProfileId = profileId ?? editProfile?.id ?? 'new';
    const returnToRoute = location?.query?.returnTo;

    if (returnToRoute) {
      dispatch(routerActions.replace({ name: returnToRoute }));
      return;
    }

    dispatch(routerActions.replace({
      name: 'profile-edit',
      params: { profileId: targetProfileId },
    }));
  };

  const handleSelectAvatar = (imageUrl?: string) => {
    const draft = {
      ...(editProfile || {}),
      imageUrl: imageUrl ?? editProfile?.imageUrl ?? null,
    } as ProfileType;
    setEditProfile(draft);
    navigateToEditProfile();
  };

  if (profilesLoading) {
    return (
      <ProfilesLayout>
        <Box column alignItems="center" justifyContent="center" p="xlarge">
          <Spinner size={3} />
        </Box>
      </ProfilesLayout>
    );
  }

  if (profilesError) {
    return <ProfilesLayout />;
  }

  if (!profileId || !editProfile) {
    if (profiles?.find(p => p.id === profileId)) {
      return <ProfilesLayout />;
    }
    return (
      <ProfilesLayout>
        <Box column alignItems="center" justifyContent="center" p="xlarge" gap="medium">
          <Heading as="h1" fontSize="2rem">
            <Text id="profiles.pickAvatar" />
          </Heading>
          <Text
            id={!profileId ? 'profiles.profileIdRequired' : 'profiles.profileNotFound'}
            color="error"
          />
          {!profileId ? null : (
            <PrimaryButton onClick={refreshProfiles}>
              <Text id="retry" />
            </PrimaryButton>
          )}
        </Box>
      </ProfilesLayout>
    );
  }

  if (avatarsLoading || avatarsError) {
    return <ProfilesLayout />;
  }

  return (
    <ProfilesLayout key="list" hideInitially>
      <Metadata title="profiles.pickAvatar" />

      <Box my="large" alignItems="center">
        <Heading id="profiles.pickAvatar" />
      </Box>

      <Box fullWidth>
        <Box
          mx="auto"
          my="xxxlarge"
          xs-my="0"
          xs-pb="giant"
          gap="medium"
          row
          wrap="wrap"
          alignItems="center"
          innerRef={containerRef}
          {...containerStyle}
        >
          {avatars.map(a => (
            <Box
              key={`${a.group}-${a.name}`}
              column
              width={PROFILE_AVATAR_SIZE}
              xs-width={PROFILE_AVATAR_XS_SIZE}
            >
              <AvatarImage
                src={a.imageUrl}
                size={PROFILE_AVATAR_SIZE}
                xsSize={PROFILE_AVATAR_XS_SIZE}
                hover
                alt={a.name}
                onClick={() => handleSelectAvatar(a.imageUrl)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        mt="medium"
        xs-position="fixed"
        xs-bottom="0"
        xs-left="0"
        xs-right="0"
        xs-fullWidth
        xs-bg="page"
        xs-p="large"
        alignItems="center"
        row
      >
        <SecondaryButton
          variant="white"
          onClick={navigateToEditProfile}
        >
          <Text id="cancel" />
        </SecondaryButton>
      </Box>
    </ProfilesLayout>
  );
}

export default ProfileSelectAvatarView;

